import { ROUTES } from '@/constants'

import { CURRENT_USER_ID, MR_BRANCHES, SEG_LOC, SEGMENTATION, PARTY } from '../MergeRequests/mergeRequestsMockData'

export type EnvironmentId = 'DEV' | 'STG' | 'UAT' | 'PRD'

export type Environment = {
	id: EnvironmentId
	label: string
	color: string
}

export type SnapshotStructure = {
	structureId: string
	structureHistoryId: string
	type: string
	code: string
	name: string
}

export type StructureTriple = {
	base: unknown
	source: unknown
	target: unknown
}

export type ReleaseRecord = {
	id: string
	systemId: string
	name: string
	version: string
	date: string
	objects: number
	sourceBranchId: string
	createdBy: string
	immutable: boolean
	templateChanges: string[]
	snapshot: SnapshotStructure[]
}

export type DeployEnvSnapshot = {
	v: number
	date: string
	prev: number
	irreversible?: boolean
}

export type DeployMatrixRow = {
	structureId: string
	label: string
	code: string
	type: string
	system: string
	branchId: string
	headVersion: number
	templateId: string
	envs: Partial<Record<EnvironmentId, DeployEnvSnapshot | null>>
}

export type DeployReason = 'struct' | 'template' | 'first'

export const ENVIRONMENTS: Environment[] = [
	{ id: 'DEV', label: 'DEV', color: '#7A7A7A' },
	{ id: 'STG', label: 'STG', color: '#2A6FDB' },
	{ id: 'UAT', label: 'UAT', color: '#C26401' },
	{ id: 'PRD', label: 'PRD', color: '#0F9D58' },
]

export const RELEASE_BRANCHES = MR_BRANCHES

const CATALOG: Array<{ structureId: string; type: string; name: string; code: string }> = [
	{ structureId: 's1', type: 'TABLE', name: 'Segment', code: 'SEGMENT' },
	{ structureId: 's2', type: 'TABLE', name: 'Segment Location Relation', code: 'SEG_LOC_REL' },
	{ structureId: 's3', type: 'TABLE', name: 'Segmentation', code: 'SEGMENTATION' },
	{ structureId: 's4', type: 'TABLE', name: 'Party', code: 'PARTY' },
	{ structureId: 's5', type: 'TABLE', name: 'Party Address Relation', code: 'PARTY_ADDR_REL' },
	{ structureId: 's6', type: 'TABLE', name: 'Contract', code: 'CONTRACT' },
	{ structureId: 's7', type: 'TABLE', name: 'Customer', code: 'CUSTOMER' },
	{ structureId: 's8', type: 'TABLE', name: 'Metering Equipment', code: 'METERING_EQUIPMENT' },
	{ structureId: 's9', type: 'TABLE', name: 'Network Equipment', code: 'NETWORK_EQUIPMENT' },
	{ structureId: 's10', type: 'TABLE', name: 'Inventory', code: 'INVENTORY' },
	{ structureId: 's11', type: 'TABLE', name: 'Region', code: 'REGION' },
	{ structureId: 's12', type: 'TABLE', name: 'Calendar', code: 'CALENDAR' },
	{ structureId: 's13', type: 'VIEW', name: 'Region Hierarchy', code: 'REGION_HIERARCHY' },
	{ structureId: 's14', type: 'VIEW', name: 'Customer 360 View', code: 'CUSTOMER_360_VIEW' },
	{ structureId: 's15', type: 'VIEW', name: 'Segment Risk View', code: 'SEGMENT_RISK_VIEW' },
	{ structureId: 's16', type: 'MAPPING', name: 'MAP_H_SEG_LOC_REL', code: 'MAP_H_SEG_LOC_REL' },
	{ structureId: 's17', type: 'MAPPING', name: 'MAP_DW_SEGM_REF_FR', code: 'MAP_DW_SEGM_REF_FR' },
	{ structureId: 's18', type: 'MAPPING', name: 'MAP_H_PARTY', code: 'MAP_H_PARTY' },
	{ structureId: 's19', type: 'FOLDER', name: 'Asset Management', code: 'ASSET_MANAGEMENT' },
	{ structureId: 's20', type: 'FOLDER', name: 'Party', code: 'PARTY_FOLDER' },
	{ structureId: 's21', type: 'TABLE', name: 'Construction', code: 'CONSTRUCTION' },
	{ structureId: 's22', type: 'TABLE', name: 'Warehouse', code: 'WAREHOUSE' },
	{ structureId: 's23', type: 'MAPPING', name: 'MAP_H_CONTRACT', code: 'MAP_H_CONTRACT' },
	{ structureId: 's24', type: 'VIEW', name: 'Active Contracts', code: 'ACTIVE_CONTRACTS' },
]

const prng = (seed: number) => {
	let s = seed >>> 0
	return () => {
		s = (Math.imul(1664525, s) + 1013904223) >>> 0
		return s / 4294967296
	}
}

const buildSnapshot = (seed: number, dropRate = 0.06): SnapshotStructure[] => {
	const rnd = prng(seed)
	return CATALOG.filter(() => rnd() > dropRate).map((s) => ({
		structureId: s.structureId,
		type: s.type,
		code: s.code,
		name: s.name,
		structureHistoryId:
			rnd() < 0.4 ? `h${seed}_${Math.floor(rnd() * 9000)}` : `h_stable_${s.structureId}`,
	}))
}

/** Shared catalog used when creating a new release from current branch HEAD. */
export const CURRENT_HEAD_SNAPSHOT: SnapshotStructure[] = CATALOG.map((s) => ({
	structureId: s.structureId,
	type: s.type,
	code: s.code,
	name: s.name,
	structureHistoryId: `h_head_${s.structureId}`,
}))

const STRUCTURE_DIFF_BUILDERS: Record<string, () => StructureTriple> = {
	s2: SEG_LOC,
	s3: SEGMENTATION,
	s4: PARTY,
}

export const getStructureDiff = (structureId: string): StructureTriple | null => {
	const build = STRUCTURE_DIFF_BUILDERS[structureId]
	if (build) return build()
	// synthetic fallback for other modified rows
	const mkcol = (code: string, o?: Record<string, unknown>) => ({
		code,
		uuid: `u_${code}`,
		name: code,
		dataType: 'VARCHAR2(30)',
		notNullFlag: false,
		comment: '',
		...o,
	})
	const base = {
		code: 'STRUCT',
		name: 'Structure',
		description: 'Structure description.',
		comment: '',
		columns: [
			mkcol('KEY', { dataType: 'NUMBER(18)', notNullFlag: true, comment: 'PK' }),
			mkcol('CODE'),
			mkcol('NAME', { dataType: 'VARCHAR2(255)' }),
			mkcol('DESCRIPTION', { dataType: 'VARCHAR2(255)' }),
			mkcol('VALID_FROM', { dataType: 'DATE' }),
		],
	}
	const source = JSON.parse(JSON.stringify(base))
	const target = JSON.parse(JSON.stringify(base))
	source.columns.find((c: { code: string }) => c.code === 'CODE').dataType = 'VARCHAR2(40)'
	target.columns.find((c: { code: string }) => c.code === 'NAME').comment = 'Business name'
	if (structureId.charCodeAt(structureId.length - 1) % 2 === 0) {
		source.columns.push(mkcol('EXTRA_COL', { dataType: 'NUMBER(5)', comment: 'New column' }))
	}
	if (structureId.charCodeAt(structureId.length - 1) % 3 === 0) {
		target.columns = target.columns.filter((c: { code: string }) => c.code !== 'VALID_FROM')
	}
	if (structureId.charCodeAt(structureId.length - 1) % 5 === 0) {
		source.columns.find((c: { code: string }) => c.code === 'DESCRIPTION').dataType = 'CLOB'
		target.columns.find((c: { code: string }) => c.code === 'DESCRIPTION').dataType = 'VARCHAR2(4000)'
	}
	return { base, source, target }
}

export const seedReleases = (): ReleaseRecord[] => {
	const snaps = [
		buildSnapshot(1000, 0.04),
		buildSnapshot(1097, 0.08),
		buildSnapshot(1194, 0.1),
	]
	return [
		{
			id: 'rel_core_0625',
			systemId: 'gasnet',
			name: 'REL_2025_06_CORE',
			version: 'R1.0',
			date: '24/06/2025',
			objects: snaps[0].length,
			sourceBranchId: 'g_main',
			createdBy: 'mspilar',
			immutable: true,
			templateChanges: ['DDL_TEMPLATE_DEFAULT'],
			snapshot: snaps[0],
		},
		{
			id: 'rel_party_0525',
			systemId: 'gasnet',
			name: 'REL_2025_05_PARTY',
			version: 'R0.9',
			date: '12/05/2025',
			objects: snaps[1].length,
			sourceBranchId: 'g_main',
			createdBy: 'jmedilek',
			immutable: true,
			templateChanges: ['MAP_MERGE_TMPL'],
			snapshot: snaps[1],
		},
		{
			id: 'rel_base_q1',
			systemId: 'gasnet',
			name: 'REL_2025_Q1_BASELINE',
			version: 'R0.5',
			date: '31/03/2025',
			objects: snaps[2].length,
			sourceBranchId: 'g_main',
			createdBy: 'DW_CORE',
			immutable: true,
			templateChanges: [],
			snapshot: snaps[2],
		},
	]
}

const DEPLOY_DEFS: Array<[string, string[]]> = [
	[
		'gasnet',
		[
			'Segment',
			'Segment Location Relation',
			'Segmentation',
			'Segment Segment Relation',
			'Party',
			'Party Address Relation',
			'Party Role',
			'Contract',
			'Contract Party Relation',
			'Construction',
			'Construction Contract',
			'Metering Equipment',
			'Network Equipment',
			'Regulating Equipment',
			'Inventory',
			'Region',
			'Calendar',
			'Warehouse',
			'Building',
			'Customer',
			'Distribution Diagram',
			'MAP_H_SEG_LOC_REL',
			'PnL Daily',
			'STG_Customer',
		],
	],
	[
		'distribuce',
		[
			'Customer',
			'Customer Contract Relation',
			'Meter Reading',
			'STG_Customer',
			'STG_Meter',
			'Distribution Diagram',
			'AFM Inbound',
			'Consumption Point',
		],
	],
	[
		'trading',
		['Trade', 'Settlement', 'Position Rowset', 'PnL Daily', 'Exposure Diagram', 'Clearing Account'],
	],
]

const DEP_DATES = [
	'12/03/2025',
	'02/04/2025',
	'18/04/2025',
	'06/05/2025',
	'21/05/2025',
	'09/06/2025',
	'24/06/2025',
	'01/07/2025',
]

const typeOf = (name: string) =>
	name.startsWith('MAP')
		? 'mapping'
		: /Diagram/.test(name)
			? 'diagram'
			: /(Daily|View|Rowset|Reading)/.test(name)
				? 'view'
				: /(STG_|AFM)/.test(name)
					? 'api'
					: 'table'

export const DEPLOY_SYSTEMS = [
	{ id: 'gasnet', name: 'Gasnet Data platform', code: 'gasnet' },
	{ id: 'distribuce', name: 'Distribuce CZ DWH', code: 'distribuce' },
	{ id: 'trading', name: 'Trading & Settlement', code: 'trading' },
]

export const buildDeployMatrix = (): DeployMatrixRow[] => {
	const rnd = prng(424242)
	const rows: DeployMatrixRow[] = []
	let n = 0
	DEPLOY_DEFS.forEach(([system, names]) => {
		names.forEach((name) => {
			n += 1
			const type = typeOf(name)
			const tmpl =
				type === 'mapping' ? 'MAP_MERGE_TMPL' : type === 'view' ? 'VIEW_STD_TMPL' : 'DDL_TEMPLATE_DEFAULT'
			const head = 3 + Math.floor(rnd() * 22)
			const branchId =
				rnd() < 0.2
					? system === 'gasnet'
						? 'g_seg'
						: system === 'distribuce'
							? 'd_cust'
							: 't_main'
					: system === 'gasnet'
						? 'g_main'
						: system === 'distribuce'
							? 'd_main'
							: 't_main'
			const envs: DeployMatrixRow['envs'] = {}
			ENVIRONMENTS.forEach((env, ei) => {
				const roll = rnd()
				if (ei === 0) {
					envs[env.id] = { v: head, date: DEP_DATES[7], prev: head - 1 }
					return
				}
				if (ei === 2 && roll < 0.15) {
					envs[env.id] = null
					return
				}
				if (ei === 3 && roll < 0.07) {
					envs[env.id] = null
					return
				}
				const lag =
					ei === 1
						? roll < 0.65
							? 0
							: 1
						: ei === 2
							? 1 + Math.floor(rnd() * 3)
							: roll < 0.8
								? 0
								: 1 + Math.floor(rnd() * 2)
				const v = Math.max(1, head - lag)
				envs[env.id] = {
					v,
					date: DEP_DATES[Math.max(0, 7 - ei - Math.floor(rnd() * 2))],
					prev: Math.max(0, v - 1),
					irreversible: env.id === 'PRD' && roll < 0.28,
				}
			})
			rows.push({
				structureId: `ds${n}`,
				label: name,
				code: name.toUpperCase().replace(/[^A-Z0-9]+/g, '_').slice(0, 26),
				type,
				system,
				branchId,
				headVersion: head,
				templateId: tmpl,
				envs,
			})
		})
	})
	return rows
}

export const DEPLOY_MATRIX = buildDeployMatrix()

export const deployHistory = (structureId: string, envId: EnvironmentId) => {
	const row = DEPLOY_MATRIX.find((r) => r.structureId === structureId)
	const snap = row?.envs[envId]
	if (!row || !snap) return []
	const cur = snap.v
	const out: Array<{ version: number; date: string; author: string; release: string | null; type: string }> =
		[]
	for (let v = cur; v >= Math.max(1, cur - 4); v--) {
		out.push({
			version: v,
			date: DEP_DATES[Math.max(0, 7 - (cur - v) * 2)],
			author: v % 2 ? 'Spilar, Martin' : 'Medilek, Jiri',
			release: v === cur ? 'R1.0' : v === cur - 1 ? 'R0.9' : null,
			type: 'DEPLOY',
		})
	}
	return out
}

/** Extra branch labels used by the deploy matrix (beyond release branches). */
export const DEPLOY_BRANCH_NAMES: Record<string, string> = {
	g_main: 'main',
	g_seg: 'feature/segment-refresh',
	d_main: 'main',
	d_cust: 'feature/customer-360',
	t_main: 'main',
}

export const STRUCTURE_TYPE_LABEL: Record<string, { label: string; color: string }> = {
	table: { label: 'Table', color: '#1F4954' },
	view: { label: 'View', color: '#0F9D58' },
	mapping: { label: 'Mapping', color: '#C26401' },
	diagram: { label: 'Diagram', color: '#5B43C4' },
	api: { label: 'API', color: '#2A6FDB' },
}

export const REASON_META: Record<
	DeployReason,
	{ label: string; color: string; bg: string }
> = {
	struct: { label: 'New structure version', color: '#0F9D58', bg: '#E6F4EA' },
	template: { label: 'New template version', color: '#5B43C4', bg: '#EEEAFA' },
	first: { label: 'First deployment', color: '#C26401', bg: '#FEF2DC' },
}

export const computeDeployScope = (release: ReleaseRecord, envId: EnvironmentId) => {
	const rows = DEPLOY_MATRIX.filter((r) => r.system === release.systemId)
	const included: Array<{
		row: DeployMatrixRow
		reason: DeployReason
		ownChanged: boolean
		from: number | null
		to: number
		template?: string
	}> = []
	let skipped = 0
	rows.forEach((row) => {
		const dep = row.envs[envId]
		const relVer = row.headVersion
		if (!dep) {
			included.push({ row, reason: 'first', ownChanged: true, from: null, to: relVer })
			return
		}
		if (dep.v < relVer) {
			included.push({ row, reason: 'struct', ownChanged: true, from: dep.v, to: relVer })
			return
		}
		if ((release.templateChanges || []).includes(row.templateId)) {
			included.push({
				row,
				reason: 'template',
				ownChanged: false,
				template: row.templateId,
				from: dep.v,
				to: dep.v,
			})
			return
		}
		skipped += 1
	})
	const rank: Record<DeployReason, number> = { struct: 0, first: 1, template: 2 }
	included.sort(
		(a, b) => rank[a.reason] - rank[b.reason] || a.row.label.localeCompare(b.row.label, 'en'),
	)
	return { included, skipped }
}

export const envMeta = (id: string) => ENVIRONMENTS.find((x) => x.id === id) || ENVIRONMENTS[0]

export const branchNameById = (id: string) =>
	RELEASE_BRANCHES.find((x) => x.id === id)?.name ?? id

export const getReleasePath = (releaseId?: string) =>
	releaseId ? ROUTES.RELEASES_RELEASE.replace(':releaseId', releaseId) : ROUTES.RELEASES

export { CURRENT_USER_ID }
